
import Vue, { PropType } from 'vue'
import SubmenuPage from '@/components/SubmenuPage.vue'
import { Contest } from '@/model/Contest'
import { User } from '@/model/User'
import Stats from '@/components/contest/Stats.vue'
import { Statistics } from '@/model/Statistics'
import ImageGallery from '@/components/ImageGallery.vue'
import ServerPaginationList from '@/components/ServerPaginationList.vue'
import ProjectItemCard from '@/components/project/ItemCard.vue'
import ParticipateFormSelector from '@/components/contest/ParticipateFormSelector.vue'
import ParticipateForm from '@/components/contest/ParticipateForm.vue'
import TermsDialog from '@/components/contest/TermsDialog.vue'
import ConfirmSimple from '@/components/ConfirmSimple.vue'
import moment from 'moment'
import ContestWinners from '@/components/contest/ContestWinners.vue'
import Judge from '@/components/contest/Judge.vue'
import { Base64 } from 'js-base64'

export default Vue.extend({
  name: 'ContestDetails',

  components: {
    SubmenuPage,
    Stats,
    ImageGallery,
    ServerPaginationList,
    ProjectItemCard,
    ParticipateFormSelector,
    ParticipateForm,
    ConfirmSimple,
    ContestWinners,
    TermsDialog,
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$route.query.project) {
        this.participateForm = true
      }

      // participate contest back from login page
      if (this.$route.query.action == 'participate') {
        const readyToParticipate = setInterval(() => {
          if (this.contest.id && this.user.id) {
            this.participate()
            clearInterval(readyToParticipate)
          }
        })
      }
    })
  },

  props: {
    contest: {
      type: Object as PropType<Contest>,
    },
    user: {
      type: Object as PropType<User>,
    },
  },

  computed: {
    canUserParticipate: function (): boolean {
      return this.contest.userApplicationStatus === '';
    },

    isBeforeStart: function (): boolean {
      return moment().isBefore(this.contest.contestStartAt)
    },

    isComingSoon: function (): boolean {
      return moment().isBefore(this.contest.publicFrom)
    },

    isContestActive: function (): boolean {
      return moment().isAfter(this.contest.contestStartAt)
    },

    isContestFinished: function (): boolean {
      return moment().isAfter(this.contest.contestFinishedAt)
    },

    participationIsActive: function(): boolean {
      return this.isContestActive && !this.isContestFinished
    },

    statsBeforeStart: function (): Statistics[] {
      const statsBefore = [
        {
          value: this.contest.reward
            ? this.contest.reward + ' USD'
            : 'Extra prizes are coming!',
          icon: 'mdi-trophy',
          name: 'sum of<br>prizes',
        },
      ]

      if (this.isComingSoon) {
        statsBefore.push({
          value: 'Coming soon!',
          icon: 'mdi-alert-decagram',
          name: 'stay<br>tuned!',
        })

        return statsBefore
      }

      statsBefore.push(
        {
          value: moment(this.contest.contestStartAt).format('YYYY-MM-DD'),
          icon: 'mdi-file-document-edit-outline',
          name: 'participate<br>from',
        },
        {
          value: moment(this.contest.contestFinishedAt).format('YYYY-MM-DD'),
          icon: 'mdi-file-document-edit',
          name: 'participate<br>before',
        },
        {
          value: moment(this.contest.winnersDateAnnouncement).format('YYYY-MM-DD'),
          icon: 'mdi-seal',
          name: 'winners<br>announcement',
        }
      )

      return statsBefore
    },

    stats: function (): Statistics[] {
      return [
        {
          value: this.contest.reward
            ? this.contest.reward + ' USD'
            : 'Extra prizes are coming!',
          icon: 'mdi-trophy',
          name: 'sum of<br>prizes',
        },
        {
          value: moment(this.contest.contestFinishedAt).format('YYYY-MM-DD'),
          icon: 'mdi-file-document-edit',
          name: 'participate<br>before',
        },
        {
          value: this.contest.applicationsCount.toString(),
          icon: 'mdi-counter',
          name: 'submitted<br>projects',
        },
        {
          value: moment(this.contest.winnersDateAnnouncement).format('YYYY-MM-DD'),
          icon: 'mdi-seal',
          name: 'winners<br>announcement',
        },
      ]
    },

    statsFinished: function (): Statistics[] {
      return [
        {
          value: this.contest.winnersCount.toString(),
          icon: 'mdi-trophy',
          name: 'number of<br>winners',
        },
        {
          value: this.contest.applicationsCount.toString(),
          icon: 'mdi-counter',
          name: 'submitted<br>projects',
        },
        {
          value: this.contest.reward + ' USD',
          icon: 'mdi-trophy',
          name: 'sum of<br>prizes',
        },
        {
          value: moment(this.contest.winnersDateAnnouncement).format('YYYY-MM-DD'),
          icon: 'mdi-seal',
          name: 'winners<br>announcement',
        },
      ]
    },

    selectStats: function (): Statistics[] {
      if (this.contest.areWinnersPublic) {
        return this.statsFinished
      }

      if (this.isBeforeStart) {
        return this.statsBeforeStart
      }

      return this.stats
    },
  },

  data: () => ({
    participateFormSelector: false,
    participateForm: false,
    messages: {
      new: 'Your application is being reviewed.',
      accepted: 'Your application is accepted.',
    },
    participationStatusColors: {
      new: 'primary',
      accepted: 'green',
    },
    termsDialog: false,
    cancelParticipationDialog: false,
  }),

  methods: {
    initJudges: function () {
      const contestDetails = this.$refs.contestDetailsRef as any
      const judges = contestDetails.querySelectorAll('.judge-definition')

      for (let i = 0; i < judges.length; i++) {
        const judgeData = judges[i].dataset.judge
        const JudgeComponent = new Judge({
          propsData: {
            judge: JSON.parse(Base64.decode(judgeData)),
          },
        }).$mount(judges[i])
      }
    },

    cancelParticipation: function () {
      this.$api
        .put('/contests/'+ this.contest.id + '/applications/cancel_application', {})
        .then((response) => {
          (this.$refs.applicationsListRef as any).load()
          this.$emit('participationCancelled', response.data)
        })
    },

    participate: function () {
      if (!this.contest.countries.includes(this.user.country)) {
        this.$store.dispatch(
          'alert/warning',
          'Unfortunately, Tech Master Event members from ' +
            this.$store.state.countries.dictionary[this.user.country] +
            " can't participate in this contest."
        )

        return
      }

      if (this.contest.onlyLegalAge && this.userAge() < 18) {
        this.$store.dispatch(
          'alert/warning',
          'You must be 18 years or older in order to participate this contest.'
        )

        return
      }

      this.participateFormSelector = true
    },

    participateForNotLoggedUser: function() {
        this.$store.dispatch(
          'alert/warning',
          'Please log in to participate in the contest.'
        )
        this.$router.push(`/sign-in?returnTo=${this.$route.path}?action=participate`)
    },

    userAge: function () {
      const today = new Date()
      const birthday = new Date(this.user.birthdayAt)
      let age = today.getFullYear() - birthday.getFullYear()
      const m = today.getMonth() - birthday.getMonth()

      if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
        age--
      }

      return age
    },
  },
})
